import type { FeedItem } from '@knocklabs/client';
import { RiArrowRightLine, RiCheckLine, RiErrorWarningLine, RiQuestionLine, RiSparklingFill } from '@remixicon/react';

import { NotificationType } from '@shared/types';
import { useNavigate } from '@tanstack/react-router';

import { appLinks } from 'src/routing/appLinks';

import { AiGradientIcon, relativeTime } from './notificationCell.helpers';

/**
 * Loosely-typed view of the AI thread notification payloads
 * (AiThreadFailedPayload / AiThreadAwaitingClarificationPayload /
 * AiThreadCompletedPayload in shared/types). Knock feed data is untyped, so
 * every field is optional and read defensively.
 */
interface AiThreadData {
  type?: string;
  thread_id?: number;
  thread_title?: string;
  source?: 'CHAT' | 'EMAIL' | 'WHATSAPP';
  link?: string;
  // failed
  error?: string;
  // awaiting clarification
  failed_tool?: string;
  public_summary?: string;
  // completed
  linked_record?: { type: 'SO' | 'PO' | 'BILL'; id: number; slug?: string; line_count?: number };
}

/** True when this notification is an AI thread lifecycle event we render here. */
export function isAiThreadNotification(data: { type?: string } | null): boolean {
  return (
    data?.type === NotificationType.AI_THREAD_FAILED ||
    data?.type === NotificationType.AI_THREAD_AWAITING_CLARIFICATION ||
    data?.type === NotificationType.AI_THREAD_COMPLETED
  );
}

type Tone = 'error' | 'attention' | 'success';

interface Presentation {
  tone: Tone;
  /** Headline shown on row 1 next to the thread title. */
  headline: string;
  /** Secondary line: the actionable detail (error / summary / linked record). */
  detail?: string;
  /** Status accent icon + label color on the detail row. */
  accentIcon: React.ReactNode;
  accentClass: string;
  /** CTA button label. */
  cta: string;
}

const LINKED_RECORD_LABEL: Record<NonNullable<AiThreadData['linked_record']>['type'], string> = {
  SO: 'Sales order',
  PO: 'Purchase order',
  BILL: 'Voucher',
};

function describeLinkedRecord(record: NonNullable<AiThreadData['linked_record']>): string {
  const label = LINKED_RECORD_LABEL[record.type] ?? record.type;
  const reference = record.slug ?? `#${record.id}`;
  if (record.line_count === undefined) return `${label} ${reference}`;
  return `${label} ${reference} · ${record.line_count} ${record.line_count === 1 ? 'line' : 'lines'}`;
}

function present(data: AiThreadData): Presentation {
  switch (data.type) {
    case NotificationType.AI_THREAD_FAILED:
      return {
        tone: 'error',
        headline: 'couldn’t be processed',
        detail: data.error,
        accentIcon: <RiErrorWarningLine size={14} className="text-red-icon" />,
        accentClass: 'text-red-text',
        cta: 'View thread',
      };
    case NotificationType.AI_THREAD_AWAITING_CLARIFICATION:
      return {
        tone: 'attention',
        headline: 'needs your input',
        detail: data.public_summary,
        accentIcon: <RiQuestionLine size={14} className="text-amber-500" />,
        accentClass: 'text-amber-600',
        cta: 'Review',
      };
    case NotificationType.AI_THREAD_COMPLETED:
    default:
      return {
        tone: 'success',
        headline: 'completed',
        detail: data.linked_record ? describeLinkedRecord(data.linked_record) : undefined,
        accentIcon: <RiCheckLine size={14} className="text-green-icon" />,
        accentClass: 'text-green-700',
        cta: 'View thread',
      };
  }
}

interface AiThreadNotificationCellProps {
  item: FeedItem;
  onMarkAsRead?: () => void;
}

export default function AiThreadNotificationCell({ item, onMarkAsRead }: AiThreadNotificationCellProps) {
  const navigate = useNavigate();
  const data = (item.data ?? {}) as AiThreadData;
  const isRead = !!item.read_at;
  const p = present(data);

  const handleClick = () => {
    if (data.thread_id != null) {
      void navigate(appLinks.aiInboxThread({ threadId: String(data.thread_id) }));
    } else {
      void navigate(appLinks.aiInbox());
    }
  };

  return (
    <div
      onClick={handleClick}
      className="group relative flex flex-row items-start px-5 py-4 gap-3 cursor-pointer hover:bg-neutral-2"
    >
      {/* Unread dot */}
      {!isRead && <span className="absolute left-2 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-green-600" />}

      {/* Avatar/Icon badge — AI gradient marks this as an AI Inbox thread */}
      <div className="flex items-center justify-center w-8 h-8 rounded-full border border-neutral-5 bg-white shrink-0">
        <AiGradientIcon>
          <RiSparklingFill size={16} />
        </AiGradientIcon>
      </div>

      {/* Content */}
      <div className="flex flex-col gap-1.5 flex-1 min-w-0">
        {/* Row 1: thread title + headline */}
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="text-14 font-medium leading-5 tracking-[-0.1px] text-gray-900 truncate">
            {data.thread_title ?? 'AI thread'}
          </span>
          <span className="text-14 leading-5 tracking-[-0.1px] text-neutral-alpha-9 shrink-0">{p.headline}</span>
        </div>

        {/* Row 2: actionable detail with status accent */}
        {p.detail && (
          <div className="flex items-start gap-1">
            <span className="mt-0.5 shrink-0">{p.accentIcon}</span>
            <span className={`text-12 leading-4 font-medium ${p.accentClass} line-clamp-2`}>{p.detail}</span>
          </div>
        )}
      </div>

      {/* Right column: time + actions */}
      <div className="flex flex-col items-end justify-between gap-2 shrink-0">
        <span className="text-12 leading-4 text-neutral-alpha-9 text-right">{relativeTime(item.inserted_at)}</span>
        <div className="flex items-center gap-1">
          {!isRead && onMarkAsRead && (
            <button
              title="Mark as read"
              onClick={(e) => {
                e.stopPropagation();
                onMarkAsRead();
              }}
              className="hidden group-hover:flex items-center justify-center w-6 h-6 rounded-lg hover:bg-black/8 text-neutral-alpha-9 hover:text-gray-900"
            >
              <RiCheckLine size={14} />
            </button>
          )}
          <button className="flex items-center justify-center px-2 h-6 gap-1 rounded-lg bg-black/4">
            <span className="text-12 font-medium text-gray-900">{p.cta}</span>
            <RiArrowRightLine size={14} className="text-neutral-alpha-9" />
          </button>
        </div>
      </div>
    </div>
  );
}
